import { Schema } from "@effect/schema";
import {
  Address,
  ChainPublicClientService,
  PercentFromBigInt,
  ProtocolKeyService,
} from "@silo/types";
import { Effect, pipe } from "effect";
import { AssetInfo } from "./types";
import { ContractsClientError } from "../types";
import { erc20Abi, getContract } from "viem";
import {
  priceProvidersRepositoryAddress,
  siloRepositoryAddress,
} from "@silo/contract-addresses";
import {
  priceProvidersRepositoryAbi,
  siloRepositoryAbi,
} from "@silo/contract-abi-types";

export type GetViemMarketAssetMeta = ReturnType<typeof getViemMarketAssetMeta>;
export type GetViemMarketAssetMetaSuccess =
  Effect.Effect.Success<GetViemMarketAssetMeta>;
export type GetViemMarketAssetMetaError =
  Effect.Effect.Error<GetViemMarketAssetMeta>;
export type GetViemMarketAssetMetaContext =
  Effect.Effect.Context<GetViemMarketAssetMeta>;

interface GetViemMarketAssetMetaParams {
  marketAddress: Address;
  assetInfo: AssetInfo;
}

export function getViemMarketAssetMeta({
  marketAddress,
  assetInfo,
}: GetViemMarketAssetMetaParams) {
  return Effect.gen(function* () {
    const protocolKey = yield* ProtocolKeyService;
    const { publicClient } = yield* ChainPublicClientService;
    const {
      tokenAddress,
      collateralShareToken,
      protectedShareToken,
      debtShareToken,
    } = assetInfo;

    const erc20Contract = getContract({
      address: tokenAddress,
      abi: erc20Abi,
      client: publicClient,
    });

    const priceProvidersRepositoryContract = getContract({
      address: priceProvidersRepositoryAddress[protocolKey],
      abi: priceProvidersRepositoryAbi,
      client: publicClient,
    });

    const siloRepositoryContract = getContract({
      address: siloRepositoryAddress[protocolKey],
      abi: siloRepositoryAbi,
      client: publicClient,
    });

    const viemMarketAssetMetaEncoded = yield* Effect.tryPromise({
      try: async () => {
        const [rawName, rawSymbol, decimals, priceProvider, maxLtv, lt] =
          await Promise.all([
            erc20Contract.read.name(),
            erc20Contract.read.symbol(),
            erc20Contract.read.decimals(),
            priceProvidersRepositoryContract.read.priceProviders([
              tokenAddress,
            ]),
            siloRepositoryContract.read.getMaximumLTV([
              marketAddress,
              tokenAddress,
            ]),
            siloRepositoryContract.read.getLiquidationThreshold([
              marketAddress,
              tokenAddress,
            ]),
          ]);

        const encoded: ViemMarketAssetMetaEncoded = {
          tokenAddress,
          rawName,
          rawSymbol,
          decimals,

          priceProvider,
          collateralShareToken,
          protectedShareToken,
          debtShareToken,
          maxLtv,
          lt,
        };

        return encoded;
      },
      catch: (cause) => new ContractsClientError({ cause }),
    });

    const viemMarketAssetMeta = yield* pipe(
      viemMarketAssetMetaEncoded,
      Schema.decode(ViemMarketAssetMeta)
    );

    return viemMarketAssetMeta;
  });
}

export type ViemMarketAssetMeta = typeof ViemMarketAssetMeta.Type;
export type ViemMarketAssetMetaEncoded = typeof ViemMarketAssetMeta.Encoded;

export const ViemMarketAssetMeta = Schema.Struct({
  tokenAddress: Address,
  rawSymbol: Schema.String,
  rawName: Schema.String,
  decimals: Schema.Number,

  priceProvider: Address,
  collateralShareToken: Address,
  protectedShareToken: Address,
  debtShareToken: Address,
  maxLtv: PercentFromBigInt,
  lt: PercentFromBigInt,
}).annotations({ identifier: "ViemMarketAssetMeta" });
